import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Rotate3d, MousePointer2 } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import ServiceExplorer from "@/components/ServiceExplorer";
import ProcessTimeline from "@/components/ProcessTimeline";
import ReviewsSection from "@/components/ReviewsSection";
import TeamStrip from "@/components/TeamStrip";
import CTASection from "@/components/CTASection";
import { TourModal } from "@/components/TourExplorer";
import { CLIENTS, COMPANY, PRINCIPLES } from "@/data/site";
import { INSIGHTS } from "@/data/insights";
import { useTours } from "@/data/useTours";

const AD_FLOW = ["Audience", "Campaign", "Creative", "Landing Experience", "Lead", "Optimization"];
const SEO_FLOW = ["Search", "Keyword Research", "Website Optimization", "Content", "Local Visibility", "Ongoing Improvement"];

const WhoWeAre = () => (
  <section data-testid="who-we-are-section" className="relative bg-navy grain">
    <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-40">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-3">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-electric font-medium">Who We Are</p>
          </Reveal>
        </div>
        <div className="lg:col-span-9">
          <Reveal delay={0.1}>
            <h2 className="font-display font-extrabold tracking-tighter leading-[1.05] text-3xl md:text-5xl lg:text-6xl text-mist max-w-4xl">
              Digital strategy built to help businesses <span className="text-electric">move forward.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
            <Reveal delay={0.18}>
              <p className="text-slatec leading-relaxed">
                Click World Online is a digital partner for businesses that take their growth seriously. We combine
                strategy, creativity, technology and performance-focused marketing into one connected way of working.
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <p className="text-slatec leading-relaxed">
                Established in 2017 and based in Mansa, Punjab, we work with businesses locally and beyond — building digital presence that is
                planned, crafted and continuously improved. Not noise. Not guesswork. Strategy that clicks.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.32}>
            <Link to="/about" data-testid="who-we-are-link" className="mt-10 inline-flex items-center gap-2 text-electric font-semibold hover:gap-3 transition-[gap] duration-300">
              Learn about us <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const WhatWeDo = () => (
  <section data-testid="what-we-do-section" className="relative bg-navy-deep">
    <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-40">
      <SectionHeading
        eyebrow="What We Do"
        title={<>Digital solutions designed around <span className="text-electric">how your business grows.</span></>}
        text="Twelve focused services, one connected system. Explore each discipline — and how it fits your business."
      />
      <div className="mt-16 lg:mt-24">
        <ServiceExplorer />
      </div>
      <Reveal className="mt-14">
        <Link to="/services" data-testid="what-we-do-all-link" className="inline-flex items-center gap-2 border border-white/20 text-mist hover:border-electric hover:text-electric font-semibold px-8 py-4 rounded-full transition-colors duration-300">
          View all services <ArrowUpRight size={18} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const WhyCWO = () => (
  <section data-testid="why-section" className="relative bg-mist text-navy dot-grid-light">
    <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-40">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-brand font-medium mb-6">Why Click World Online</p>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="font-display font-black tracking-tighter leading-[0.98] text-4xl md:text-6xl lg:text-8xl max-w-5xl">
          DIFFERENT BUSINESSES.<br />
          <span className="text-outline" style={{ WebkitTextStroke: "1px rgba(10,22,51,0.35)" }}>DIFFERENT STRATEGIES.</span>
        </h2>
      </Reveal>
      <div className="mt-16 lg:mt-24 border-t border-navy/10">
        {PRINCIPLES.map((p, i) => (
          <Reveal key={p.title} delay={0.04 * i}>
            <div
              data-testid={`principle-${p.title.toLowerCase()}`}
              className="group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 py-8 lg:py-10 border-b border-navy/10 items-baseline hover:bg-navy/[0.03] transition-colors duration-300 px-2 md:px-4 -mx-2 md:-mx-4"
            >
              <span className="md:col-span-1 font-display text-sm font-bold text-brand">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="md:col-span-4 font-display text-2xl lg:text-4xl font-extrabold tracking-tight group-hover:text-brand transition-colors duration-300">
                {p.title}
              </h3>
              <p className="md:col-span-7 text-navy/70 leading-relaxed max-w-xl">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const SelectedWork = () => (
  <section data-testid="selected-work-section" className="relative bg-navy-deep grain">
    <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-40">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <SectionHeading
          eyebrow="Selected Work"
          title={<>Brands we've helped <span className="text-electric">click.</span></>}
          text="Trusted by 800+ businesses since 2017 — a selection of brands that rely on Click World Online for their digital presence."
        />
        <Reveal delay={0.2}>
          <Link to="/portfolio" data-testid="work-view-all" className="inline-flex items-center gap-2 border border-white/20 text-mist hover:border-electric hover:text-electric font-semibold px-8 py-4 rounded-full transition-colors duration-300 shrink-0">
            View Our Work <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </div>
      <div className="mt-16 border-t border-white/5">
        {CLIENTS.slice(0, 5).map((c, i) => (
          <Reveal key={c.slug} delay={0.04 * i}>
            <Link
              to={`/portfolio/${c.slug}`}
              data-testid={`work-row-${c.slug}`}
              className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 lg:gap-10 py-7 lg:py-9 border-b border-white/5 px-2 lg:px-6 -mx-2 lg:-mx-6 hover:bg-white/[0.03] transition-colors duration-300"
            >
              <span className="font-display text-sm font-bold text-mist/30 group-hover:text-electric transition-colors duration-300">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>
                <span className="block font-display text-2xl lg:text-5xl font-extrabold tracking-tight text-mist group-hover:text-electric transition-colors duration-300">
                  {c.name}
                </span>
                <span className="block mt-1 text-sm text-slatec">{c.category}</span>
              </span>
              <ArrowUpRight size={26} className="text-mist/30 group-hover:text-electric group-hover:translate-x-1 group-hover:-translate-y-1 transition-[transform,color] duration-300" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FlowCard = ({ title, steps, link, linkLabel, testid }) => (
  <div data-testid={testid} className="relative bg-navy-light/60 backdrop-blur border border-white/10 rounded-3xl p-8 lg:p-12 overflow-hidden grain">
    <div className="absolute -top-24 -right-24 w-64 h-64 bg-brand/25 blur-[110px] rounded-full" aria-hidden="true" />
    <div className="relative">
      <h3 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-mist">{title}</h3>
      <div className="mt-8 flex flex-wrap items-center gap-y-4">
        {steps.map((s, i) => (
          <span key={s} className="flex items-center">
            <span className="text-sm lg:text-base font-medium text-mist/85 border border-white/10 bg-white/5 rounded-full px-4 py-2">
              {s}
            </span>
            {i < steps.length - 1 && <span className="mx-2 text-electric" aria-hidden="true">→</span>}
          </span>
        ))}
      </div>
      <Link to={link} data-testid={`${testid}-link`} className="mt-8 inline-flex items-center gap-2 text-electric font-semibold hover:gap-3 transition-[gap] duration-300">
        {linkLabel} <ArrowUpRight size={18} />
      </Link>
    </div>
  </div>
);

const Ecosystems = () => (
  <section data-testid="ecosystems-section" className="relative bg-navy">
    <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-40">
      <SectionHeading
        eyebrow="Performance Channels"
        title={<>Advertising &amp; search, <span className="text-electric">engineered as ecosystems.</span></>}
        text="Google Ads, Meta advertising and SEO work best as systems — every stage connected, every click accounted for."
      />
      <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
        <Reveal>
          <FlowCard title="Google & Meta Advertising" steps={AD_FLOW} link="/services/google-ads" linkLabel="Explore advertising" testid="ad-ecosystem" />
        </Reveal>
        <Reveal delay={0.12}>
          <FlowCard title="SEO & Search Visibility" steps={SEO_FLOW} link="/services/seo" linkLabel="Explore SEO" testid="seo-ecosystem" />
        </Reveal>
      </div>
    </div>
  </section>
);

const Preview360 = () => {
  const [open, setOpen] = useState(false);
  const tours = useTours();
  const strip = tours.slice(0, 12);
  return (
    <section data-testid="preview-360-section" className="relative bg-navy-deep overflow-hidden">
      <div className="max-w-8xl mx-auto px-6 lg:px-12 pt-24 lg:pt-40 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end">
          <SectionHeading
            eyebrow="360° Experiences"
            title={<>Step inside, <span className="text-electric">from anywhere.</span></>}
            text={`Immersive 360° photography and Google Street View virtual tours — ${tours.length ? tours.length : "400"}+ published experiences and counting.`}
          />
          <Reveal delay={0.15} className="flex flex-wrap gap-4 lg:justify-end">
            <Link to="/360-experiences" data-testid="preview-360-explore" className="inline-flex items-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300">
              Explore 360° Experiences <ArrowUpRight size={18} />
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="preview-360-live"
              className="inline-flex items-center gap-2 border border-white/20 text-mist hover:border-electric hover:text-electric font-semibold px-8 py-4 rounded-full transition-colors duration-300"
            >
              <Rotate3d size={18} /> Live Preview
            </button>
          </Reveal>
        </div>
      </div>
      <div className="relative pb-24 lg:pb-32">
        <div className="flex w-max animate-marquee-slow gap-5 px-5">
          {[...strip, ...strip].map((t, i) => (
            <div key={i} className="relative w-72 md:w-96 shrink-0 rounded-2xl overflow-hidden border border-white/10">
              <img src={t.thumb} alt={i < strip.length ? `360° tour — ${t.title}` : ""} loading="lazy" className="w-full h-44 md:h-52 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/80 to-transparent" aria-hidden="true" />
              <span className="absolute bottom-3 left-4 text-sm font-medium text-mist/90">{t.title}</span>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-navy-deep to-transparent" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-navy-deep to-transparent" aria-hidden="true" />
      </div>
      <AnimatePresence>
        {open && (
          <TourModal title="Live 360° Preview" embedUrl={COMPANY.featuredTourEmbed} mapsUrl={null} onClose={() => setOpen(false)} />
        )}
      </AnimatePresence>
    </section>
  );
};

const ProcessSection = () => (
  <section data-testid="process-section" className="relative bg-mist text-navy">
    <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-40">
      <SectionHeading
        dark={false}
        eyebrow="How We Work"
        title={<>A process built for <span className="text-brand">momentum.</span></>}
        text="Four stages. One direction. Every engagement moves through the same disciplined flow."
      />
      <ProcessTimeline dark={false} />
    </div>
  </section>
);

const Home = () => {
  useEffect(() => {
    document.title = "Click World Online — Digital Marketing Agency in Mansa, Punjab";
  }, []);
  return (
    <>
      <Hero />
      <Marquee
        items={["Digital Strategy", "SEO", "Google Ads", "Meta Advertising", "Social Media", "Lead Generation", "Content Creation", "Websites", "360° Virtual Tours", "Branding"]}
      />
      <WhoWeAre />
      <section data-testid="founder-section" className="relative bg-navy-deep grain overflow-hidden">
        <div className="absolute -top-24 left-1/4 w-96 h-96 bg-brand/15 blur-[130px] rounded-full" aria-hidden="true" />
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-5">
              <div className="relative max-w-sm mx-auto lg:mx-0">
                <div className="absolute -inset-3 border border-electric/20 rounded-[2rem]" aria-hidden="true" />
                <img
                  src="/assets/founder.jpg"
                  alt="Akshay Jindal, Founder of Click World Online"
                  data-testid="founder-photo"
                  loading="lazy"
                  className="relative rounded-[1.75rem] w-full aspect-[4/5] object-cover object-top border border-white/10"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-navy-deep/70 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-3" data-testid="founder-nameplate">
                  <p className="font-display font-bold text-mist text-lg leading-tight">Akshay Jindal</p>
                  <p className="text-electric text-xs uppercase tracking-[0.2em] mt-0.5">Founder, Click World Online</p>
                </div>
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-electric font-medium mb-6">From The Founder</p>
              </Reveal>
              <Reveal delay={0.1}>
                <blockquote data-testid="founder-message" className="font-display font-extrabold tracking-tight leading-[1.15] text-2xl md:text-4xl lg:text-[2.75rem] text-mist">
                  "Every business we work with is different — and that is exactly the point. Click World Online was started to bring honest, strategy-first digital marketing to businesses that deserve more than templates."
                </blockquote>
              </Reveal>
              <Reveal delay={0.18}>
                <p className="mt-6 text-slatec leading-relaxed max-w-xl">
                  Tell us where you want your business to go — we'll build the digital road that takes you there, and walk it with you.
                </p>
              </Reveal>
              <Reveal delay={0.26}>
                <p className="mt-8 font-display font-bold text-mist text-lg" data-testid="founder-name">— Akshay Jindal, Founder</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      <TeamStrip />
      <ReviewsSection />
      <WhatWeDo />
      <section data-testid="service-areas-band" className="relative bg-navy border-y border-white/5 grain overflow-hidden">
        <div className="absolute -top-24 right-1/4 w-96 h-64 bg-brand/15 blur-[120px] rounded-full" aria-hidden="true" />
        <div className="relative max-w-8xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-electric font-medium">Our Service Areas</p>
                <h2 className="mt-4 font-display font-extrabold tracking-tighter text-3xl md:text-4xl text-mist leading-tight">
                  Local trust. <span className="text-electric">International reach.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <div className="flex flex-wrap gap-2.5">
                  {["Based in India — Mansa, Punjab", "Serving businesses across India", "Remote clients in Canada, USA, UK & Australia", "Selected digital services, worldwide"].map((t) => (
                    <span key={t} className="text-sm text-mist/80 border border-white/10 bg-white/5 rounded-full px-4 py-2">
                      {t}
                    </span>
                  ))}
                </div>
                <Link to="/service-areas" data-testid="home-service-areas-link" className="mt-6 inline-flex items-center gap-2 text-electric font-semibold hover:gap-3 transition-[gap] duration-300">
                  Explore our service areas <ArrowUpRight size={18} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      <WhyCWO />
      <SelectedWork />
      <Ecosystems />
      <Preview360 />
      <ProcessSection />
      <section data-testid="insights-teaser-section" className="relative bg-navy border-t border-white/5 grain overflow-hidden">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-electric font-medium mb-4">Insights</p>
                <h2 className="font-display font-extrabold tracking-tighter text-3xl md:text-4xl text-mist leading-tight">
                  Thinking worth <span className="text-electric">reading.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              {INSIGHTS.slice(0, 1).map((a) => (
                <Reveal key={a.slug} delay={0.1}>
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`home-insight-${a.slug}`}
                    className="group flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-navy-light border border-white/10 rounded-3xl p-8 hover:border-electric/40 transition-colors duration-300"
                  >
                    <span>
                      <span className="block text-xs uppercase tracking-[0.2em] text-mist/40">
                        {a.category}
                      </span>
                      <span className="block mt-3 font-display text-xl lg:text-2xl font-extrabold tracking-tight text-mist group-hover:text-electric transition-colors duration-300 leading-snug">
                        {a.title.replace(" in Punjab", "")}
                      </span>
                    </span>
                    <ArrowUpRight size={26} className="text-mist/30 group-hover:text-electric shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-[transform,color] duration-300" />
                  </Link>
                </Reveal>
              ))}
              <Reveal delay={0.16}>
                <Link to="/insights" data-testid="home-insights-all" className="mt-6 inline-flex items-center gap-2 text-electric font-semibold hover:gap-3 transition-[gap] duration-300">
                  View all insights <ArrowUpRight size={18} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
};

export default Home;
